import React from "react";
import { Icon, StatCard, EmptyState, computeQuoteView, quoteMarginOf, lostReasonLabel, quotePayments, woEstLabor, actualHours, crewRate, woTimeLogs, WeekDigest } from "./registry";
import { HoursExportCard } from "./hoursexport";   // Payroll hours: per worker, per pay period
import { blocksOf, DROPS, rateCardsOf } from "./lib/engine.concrete";   // the concrete reports: margin by job type and by drop (§13 item 12)
import { groupMargin, suggestRecipe, liftActuals, JOB_SHORT as CX_JOB_SHORT } from "./lib/concrete.actuals";
import { verticalOf, VERTICALS } from "./lib/verticals";
import { storeCan } from "./lib/roles";
import { inPeriod, signedRevenueByJobType, rebaseBlocks, allocateHours, jobTypeRows, concreteSourceOf, servicesRollup } from "./lib/reports.concrete";   // the concrete reports (§13 item 17)
import { toCsv, downloadCsv as downloadCsvText } from "./lib/csv";   // (this file keeps an older local downloadCsv for the accountant export)
/* ============================================================
   Fence Works — Reports / Analytics
   Close-rate, revenue, and pipeline view (Jobber / Housecall Pro
   style). Everything is computed live from existing store data
   (quotes / projects / work orders) — no new persisted state and
   no chart libraries; the bars are hand-rolled CSS/SVG.
   ============================================================ */
const R_money0 = window.VFP.money0;

/* ---- period filter -------------------------------------------------------
   Each option resolves to a [from, to) epoch-ms window. "All" has no lower
   bound. A quote/project counts in a period by its createdAt timestamp. ---- */
const PERIODS = [
  { id: "month", label: "This Month" },
  { id: "quarter", label: "This Quarter" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
];
function periodStart(id: string): number {
  const now = new Date();
  if (id === "month") return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  if (id === "quarter") return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1).getTime();
  if (id === "year") return new Date(now.getFullYear(), 0, 1).getTime();
  return 0; // all time
}

const WON_STATUSES = ["signed", "paid"];   // a quote is "won" once it's signed or paid
const OPEN_STATUSES = ["pending", "viewed"]; // still in the pipeline

/* Lead-source normalization for the marketing-ROI report. Quotes carry a raw
   `source` ("widget"/"booking" from the public estimator, or one the operator
   picks in the estimator). Map raw → a canonical bucket + label; anything
   missing lands in "unknown". */
const SOURCE_MAP: Record<string, { key: string; label: string }> = {
  referral: { key: "referral", label: "Referral" },
  google:   { key: "google",   label: "Google / LSA" },
  website:  { key: "website",  label: "Website / widget" },
  widget:   { key: "website",  label: "Website / widget" },
  booking:  { key: "booking",  label: "Online booking" },
  social:   { key: "social",   label: "Facebook / Instagram" },
  repeat:   { key: "repeat",   label: "Repeat customer" },
  yardsign: { key: "yardsign", label: "Yard sign" },
  homeshow: { key: "homeshow", label: "Home show" },
  other:    { key: "other",    label: "Other" },
};
function srcBucket(raw: any) { return SOURCE_MAP[(raw || "").toString().toLowerCase()] || { key: "unknown", label: "Unknown / not tagged" }; }
// months covered by a period id (drives spend → CAC/ROAS scaling)
function periodMonths(id: string, quotes: any[]): number {
  if (id === "month") return 1;
  if (id === "quarter") return 3;
  if (id === "year") return 12;
  // "all": span from the earliest quote to now, at least 1 month
  const ats = quotes.map(q => Number(q.createdAt) || 0).filter(Boolean);
  if (!ats.length) return 1;
  const months = (Date.now() - Math.min(...ats)) / (1000 * 60 * 60 * 24 * 30.4);
  return Math.max(1, Math.round(months));
}

/* Resolve a fence-type / category label for a quote or project. Prefers the
   product catalog category (built-ins + synced custom products live on
   store.allProducts); falls back to the product name, then "Other". */
function catOf(row: any, products: any[]): string {
  const p = row && row.productId ? (products || []).find(x => x.id === row.productId) : null;
  return (p && p.category) || (row && row.productName) || "Other";
}

/* Money actually collected on a quote — manual payments + online (Stripe)
   payments, falling back to depositPaid. Used only as a "collected" signal;
   won revenue itself is the quote total once signed/paid. */
function collectedOn(q: any): number {
  const manual = (Array.isArray(q.payments) ? q.payments : []).reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
  const online = (Array.isArray(q.stripePayments) ? q.stripePayments : []).reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
  // depositPaid already holds the Stripe sum (webhook) — don't double-count
  // it against the itemized stripePayments ledger; take whichever is larger.
  return manual + Math.max(online, Number(q.depositPaid) || 0);
}

function ReportsPage({ store, go }: any) {
  const [period, setPeriod] = React.useState<any>("year");

  const report = React.useMemo(() => {
    const products = store.allProducts || (window.VFP && window.VFP.PRODUCTS) || [];
    const from = periodStart(period);
    const quotes = (store.quotes || []).filter((q: any) => (Number(q.createdAt) || 0) >= from);
    const projects = (store.projects || []).filter((p: any) => (Number(p.createdAt) || 0) >= from);
    const workOrders = store.workOrders || [];

    // ---- headline figures ----
    const quotedTotal = quotes.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
    const wonQuotes = quotes.filter((q: any) => WON_STATUSES.includes(q.status));
    const wonTotal = wonQuotes.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
    const collected = quotes.reduce((a: number, q: any) => a + collectedOn(q), 0);

    // win rate / close rate: won quotes ÷ all quotes in the window
    const winRate = quotes.length ? Math.round((wonQuotes.length / quotes.length) * 100) : 0;
    const avgJob = wonQuotes.length ? wonTotal / wonQuotes.length : 0;

    // ---- why we lose (period-bound): lost quotes rolled up by reason ----
    // decidedRate = won ÷ (won + lost) — the close rate on deals that actually
    // reached a verdict, so open quotes stop diluting the number.
    const lostQuotes = quotes.filter((q: any) => q.status === "lost");
    const lostTotal = lostQuotes.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
    const lostAgg: Record<string, any> = {};
    lostQuotes.forEach((q: any) => {
      const key = q.lostReason || "untagged";
      const rec = lostAgg[key] || (lostAgg[key] = { key, label: lostReasonLabel(q.lostReason), count: 0, total: 0 });
      rec.count += 1; rec.total += Number(q.total) || 0;
    });
    const lostReasons = Object.values(lostAgg).sort((a: any, b: any) => b.count - a.count || b.total - a.total);
    const decided = wonQuotes.length + lostQuotes.length;
    const decidedRate = decided ? Math.round((wonQuotes.length / decided) * 100) : null;

    // ---- revenue by month (last 12 calendar months, won quotes by the date
    //      they were WON — wonAt, falling back to lastSentAt / createdAt for
    //      quotes from before wonAt was stamped) ----
    const wonAtOf = (q: any) => Number(q.wonAt) || Number(q.lastSentAt) || Number(q.createdAt) || 0;
    const now = new Date();
    const months: { key: string; label: string; total: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: d.getFullYear() + "-" + d.getMonth(), label: d.toLocaleDateString("en-US", { month: "short" }), total: 0 });
    }
    const monthIndex: Record<string, number> = {};
    months.forEach((m, i) => { monthIndex[m.key] = i; });
    // month chart spans the trailing year regardless of the period filter, but
    // only counts won quotes that fall inside the selected window.
    (store.quotes || []).filter((q: any) => WON_STATUSES.includes(q.status) && wonAtOf(q) >= from).forEach((q: any) => {
      const d = new Date(wonAtOf(q));
      const key = d.getFullYear() + "-" + d.getMonth();
      if (key in monthIndex) months[monthIndex[key]].total += Number(q.total) || 0;
    });
    const monthMax = months.reduce((m, x) => Math.max(m, x.total), 0);

    // ---- revenue by fence type (won quotes, grouped by product category) ----
    const byCat: Record<string, { total: number; count: number }> = {};
    wonQuotes.forEach((q: any) => {
      const c = catOf(q, products);
      (byCat[c] || (byCat[c] = { total: 0, count: 0 }));
      byCat[c].total += Number(q.total) || 0;
      byCat[c].count += 1;
    });
    const cats = Object.keys(byCat).map(k => ({ name: k, ...byCat[k] })).sort((a, b) => b.total - a.total);
    const catMax = cats.reduce((m, c) => Math.max(m, c.total), 0);

    // ---- pipeline (NOT period-bound — these are live, current open items) ----
    const openQuotes = (store.quotes || []).filter((q: any) => OPEN_STATUSES.includes(q.status));
    const openCount = openQuotes.length;
    const openValue = openQuotes.reduce((a: number, q: any) => a + (Number(q.total) || 0), 0);
    const scheduled = workOrders.filter((w: any) => w.status === "scheduled");
    const inProgress = workOrders.filter((w: any) => w.status === "in_progress");
    const backlogValue = [...scheduled, ...inProgress].reduce((a: number, w: any) => a + (Number(w.total) || 0), 0);

    // ---- lead sources & marketing ROI (period-bound) ----
    const spend = (store.settings && store.settings.marketingSpend) || {};
    const pMonths = periodMonths(period, store.quotes || []);
    const srcAgg: Record<string, any> = {};
    quotes.forEach((q: any) => {
      // a concrete quote's source lives in its request (the website's heard-from, a care plan's rebook, the AI line) — lib/reports.concrete.ts
      const b = verticalOf(q) === "concrete" ? concreteSourceOf(q) : srcBucket(q.source);
      const rec = srcAgg[b.key] || (srcAgg[b.key] = { key: b.key, label: b.label, leads: 0, won: 0, revenue: 0 });
      rec.leads += 1;
      if (WON_STATUSES.includes(q.status)) { rec.won += 1; rec.revenue += Number(q.total) || 0; }
    });
    const sources = Object.values(srcAgg).map((s: any) => {
      const monthly = Number(spend[s.key]) || 0;
      const periodSpend = monthly * pMonths;
      return { ...s, closeRate: s.leads ? Math.round((s.won / s.leads) * 100) : 0, avgJob: s.won ? s.revenue / s.won : 0,
        monthlySpend: monthly, periodSpend,
        cac: (periodSpend > 0 && s.won > 0) ? periodSpend / s.won : null,
        roas: periodSpend > 0 ? s.revenue / periodSpend : null };
    }).sort((a: any, b: any) => b.revenue - a.revenue || b.leads - a.leads);
    const anyTagged = sources.some((s: any) => s.key !== "unknown");

    // ---- pricing coach: quoted margin overall + by category — after any
    // discount (quoteMarginOf), the same margin the quote screen and the Jobs
    // Sheet show, so the three never disagree about one quote ----
    const marginByCat: Record<string, any> = {};
    let marginSum = 0, marginN = 0;
    quotes.forEach((q: any) => {
      try {
        const view = computeQuoteView(q, store.settings || {}, products);
        const mm = view && view.quote ? quoteMarginOf(view.quote) : null;
        const m = mm ? mm.pct : null;
        if (m != null && Number.isFinite(m)) {
          marginSum += m; marginN += 1;
          const c = catOf(q, products);
          const rec = marginByCat[c] || (marginByCat[c] = { cat: c, sum: 0, n: 0 });
          rec.sum += m; rec.n += 1;
        }
      } catch (e) { /* skip a quote the engine can't price */ }
    });
    const avgMargin = marginN ? marginSum / marginN : 0;
    const catMargins = Object.values(marginByCat).map((r: any) => ({ cat: r.cat, margin: r.sum / r.n, n: r.n })).sort((a: any, b: any) => a.margin - b.margin);

    return {
      quoteCount: quotes.length, quotedTotal, wonCount: wonQuotes.length, wonTotal, collected,
      winRate, avgJob, months, monthMax, cats, catMax,
      openCount, openValue, schedCount: scheduled.length, inProgCount: inProgress.length, backlogValue,
      sources, anyTagged, pMonths, avgMargin, catMargins,
      lostCount: lostQuotes.length, lostTotal, lostReasons, decidedRate,
      hasAny: (store.quotes || []).length > 0 || (store.projects || []).length > 0,
      // the all-services roll-up (§13 item 17): both services side by side, whichever lens is on — over the unscoped arrays
      rollup: (store.enabledLenses || []).length > 1 ? servicesRollup(store.allQuotes || store.quotes || [], store.allWorkOrders || store.workOrders || [], from, verticalOf, (q: any) => WON_STATUSES.includes(q.status)) : [],
      from,
    };
  }, [store.quotes, store.projects, store.workOrders, store.allQuotes, store.allWorkOrders, store.allProducts, store.settings, period]);

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="display" data-tour="reports-head" style={{ fontSize: 28, margin: 0 }}>Reports</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Close rate, revenue, and pipeline — the numbers that tell you how the business is doing.</p>
        </div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {PERIODS.map(p => (
            <button key={p.id} className={"filter-chip" + (period === p.id ? " on" : "")} onClick={() => setPeriod(p.id)}>{p.label}</button>
          ))}
        </div>
      </div>

      <WeekDigest store={store} go={go} />

      {!report.hasAny ? (
        <div className="card pad">
          <EmptyState icon="send" title="No data to report yet" sub="Send a few estimates and win some jobs — your close rate, revenue, and pipeline will show up here." />
          <div className="row" style={{ justifyContent: "center", marginTop: 8 }}>
            <button className="btn btn-primary" onClick={() => go("estimator")}><Icon name="plus" size={15} />Create an Estimate</button>
          </div>
        </div>
      ) : (
        <>
          {/* ---- headline cards ---- */}
          <div className="stat-row" style={{ marginBottom: 14 }}>
            <StatCard icon="check" value={R_money0(report.wonTotal)} label="Total Won" accent="var(--green)" />
            <StatCard icon="dollar" value={R_money0(report.quotedTotal)} label="Total Quoted" accent="var(--cyan)" />
            <StatCard icon="target" value={report.winRate + "%"} label="Win Rate" accent="var(--teal)" />
            <StatCard icon="bolt" value={R_money0(report.avgJob)} label="Avg Job Size" accent="var(--violet)" />
          </div>

          {/* ---- labor: estimated vs clocked, per finished job ---- */}
          {(() => {
            const crews = (store.settings && store.settings.crews) || [];
            const rows = (store.workOrders || []).filter((w: any) => woTimeLogs(w).length > 0).map((w: any) => {
              const est = woEstLabor(w, store, crews); const act = actualHours(w); const rate = crewRate(crews.find((c: any) => c.id === w.crewId));
              return { id: w.id, name: w.customer || w.title || "Job", est: est.hours, act, dH: act - est.hours, dC: (act - est.hours) * rate, notes: woTimeLogs(w).map((e: any) => e.note).filter(Boolean).join(" · "), gps: woTimeLogs(w).some((e: any) => e.gps && e.gps.override) };
            });
            if (!rows.length) return null;
            const estT = rows.reduce((a: number, r: any) => a + r.est, 0), actT = rows.reduce((a: number, r: any) => a + r.act, 0), dC = rows.reduce((a: number, r: any) => a + r.dC, 0);
            const pct = estT > 0 ? Math.round((actT / estT) * 100) : 0;
            return (
              <div className="card pad" data-tour="labor-actuals" style={{ marginBottom: 14 }}>
                <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                  <div className="kicker">Labor — estimated vs clocked</div>
                  <span className="muted" style={{ fontSize: 12 }}>{rows.length} job{rows.length === 1 ? "" : "s"} with hours · {actT.toFixed(1)} hr clocked vs {estT.toFixed(1)} hr quoted · <b style={{ color: pct > 100 ? "var(--amber)" : "var(--green)" }}>{pct}%</b> of estimate · {dC >= 0 ? "+" : "−"}{R_money0(Math.abs(dC))} labor {dC >= 0 ? "over" : "under"}</span>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  {rows.slice(0, 6).map((r: any) => (
                    <div key={r.id} className="row gap-8" style={{ fontSize: 13, alignItems: "baseline", cursor: "pointer" }} onClick={() => go("workorders", { openWorkOrder: r.id })}>
                      <span style={{ flex: "1 1 160px", fontWeight: 600 }}>{r.name}</span>
                      <span className="muted tnum">{r.est.toFixed(1)} hr quoted → {r.act.toFixed(1)} hr clocked</span>
                      <span className="tnum" style={{ color: r.dH > 0.001 ? "var(--amber)" : "var(--green)", fontWeight: 700 }}>{r.dH >= 0 ? "+" : ""}{r.dH.toFixed(1)} hr · {r.dC >= 0 ? "+" : "−"}{R_money0(Math.abs(r.dC))}</span>
                      {r.gps && <span className="chip" style={{ fontSize: 10, color: "var(--violet, #a78bfa)", borderColor: "var(--line-2)" }}>GPS override</span>}
                      {r.notes && <span className="faint" style={{ flexBasis: "100%", fontSize: 11.5 }}>{r.notes}</span>}
                    </div>
                  ))}
                </div>
                <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Quoted hours come from the estimate's labor model; clocked hours from Crew Mode and the office's log. Jobs that run long here are where the labor settings (Settings → Pricing) need a nudge.</p>
              </div>
            );
          })()}

          <div className="rep-grid">
            {/* ---- close rate ---- */}
            <div className="card pad rep-close">
              <div className="kicker" style={{ marginBottom: 14 }}>Close Rate</div>
              <CloseRate winRate={report.winRate} won={report.wonCount} quoted={report.quoteCount} />
              <div className="rep-close-foot">
                <div><div className="muted" style={{ fontSize: 12 }}>Won</div><div className="display tnum" style={{ fontSize: 19, color: "var(--green)" }}>{report.wonCount}</div></div>
                <div><div className="muted" style={{ fontSize: 12 }}>Quoted</div><div className="display tnum" style={{ fontSize: 19 }}>{report.quoteCount}</div></div>
                <div><div className="muted" style={{ fontSize: 12 }}>Collected</div><div className="display tnum" style={{ fontSize: 19, color: "var(--teal)" }}>{R_money0(report.collected)}</div></div>
              </div>
            </div>

            {/* ---- pipeline ---- */}
            <div className="card pad">
              <div className="kicker" style={{ marginBottom: 14 }}>Pipeline (Live)</div>
              <div className="rep-pipe">
                <PipeRow icon="send" label="Open quotes" sub={report.openCount + " awaiting response"} value={R_money0(report.openValue)} accent="var(--cyan)" />
                <PipeRow icon="calendar" label="Scheduled jobs" sub={report.schedCount + " on the calendar"} value={String(report.schedCount)} accent="var(--blue)" />
                <PipeRow icon="bolt" label="In progress" sub={report.inProgCount + " crews working"} value={String(report.inProgCount)} accent="var(--violet)" />
                <PipeRow icon="clipboard" label="Booked backlog" sub="Scheduled + in-progress value" value={R_money0(report.backlogValue)} accent="var(--green)" last />
              </div>
            </div>
          </div>

          {/* ---- revenue by month ---- */}
          <div className="card pad" style={{ marginTop: 14 }}>
            <div className="row between" style={{ marginBottom: 16 }}>
              <div className="kicker">Revenue by Month</div>
              <div className="muted" style={{ fontSize: 12 }}>Won revenue · trailing 12 months</div>
            </div>
            {report.monthMax <= 0 ? (
              <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: "26px 0", margin: 0 }}>No won revenue in this window yet.</p>
            ) : (
              <div className="rep-bars">
                {report.months.map((m, i) => {
                  const pct = report.monthMax > 0 ? (m.total / report.monthMax) * 100 : 0;
                  return (
                    <div key={i} className="rep-bar-col" title={m.label + " · " + R_money0(m.total)}>
                      <div className="rep-bar-val">{m.total > 0 ? R_money0(m.total) : ""}</div>
                      <div className="rep-bar-track">
                        <div className="rep-bar-fill" style={{ height: Math.max(pct, m.total > 0 ? 3 : 0) + "%" }} />
                      </div>
                      <div className="rep-bar-lbl">{m.label}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---- revenue by fence type (the Concrete lens has "By job type" below instead) ---- */}
          {store.lens !== "concrete" && <div className="card pad" style={{ marginTop: 14 }}>
            <div className="row between" style={{ marginBottom: 16 }}>
              <div className="kicker">Revenue by Fence Type</div>
              <div className="muted" style={{ fontSize: 12 }}>Won jobs by product category</div>
            </div>
            {report.cats.length === 0 ? (
              <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: "26px 0", margin: 0 }}>No won jobs in this window yet.</p>
            ) : (
              <div style={{ display: "grid", gap: 13 }}>
                {report.cats.map((c, i) => {
                  const pct = report.catMax > 0 ? Math.round((c.total / report.catMax) * 100) : 0;
                  const share = report.wonTotal > 0 ? Math.round((c.total / report.wonTotal) * 100) : 0;
                  const color = CAT_COLORS[i % CAT_COLORS.length];
                  return (
                    <div key={c.name}>
                      <div className="row between" style={{ marginBottom: 6, fontSize: 13.5 }}>
                        <span className="row gap-8" style={{ fontWeight: 600 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: color }} />{c.name}
                          <span className="muted" style={{ fontWeight: 400 }}>· {c.count} job{c.count === 1 ? "" : "s"}</span>
                        </span>
                        <span className="tnum" style={{ fontWeight: 700 }}>{R_money0(c.total)} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>({share}%)</span></span>
                      </div>
                      <div className="rep-hbar"><div className="rep-hbar-fill" style={{ width: Math.max(pct, 2) + "%", background: color }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>}

          {/* ---- the concrete lens: revenue, cost, margin, hours and foam per job type; lifts by drop (§13 item 17) ---- */}
          {store.lens === "concrete" && <ConcreteJobTypeCard store={store} from={report.from} period={period} />}

          {/* ---- lead sources & marketing ROI ---- */}
          <LeadSourceCard report={report} store={store} />

          {/* ---- both services side by side (§13 item 17) ---- */}
          {report.rollup.length > 0 && <AllServicesCard report={report} />}

          {/* ---- why we lose: reasons behind every dead deal ---- */}
          <WhyWeLoseCard report={report} />

          {/* ---- pricing coach ---- */}
          <PricingCoachCard report={report} />

          {/* ---- accountant export: invoices & payments CSV ---- */}
          <AccountantExportCard store={store} />

          {/* ---- payroll hours: per worker, per pay period (hoursexport.tsx) ---- */}
          <HoursExportCard store={store} />

          {/* ---- estimate accuracy: quoted vs actual, learning loop (a fence knob — the labor rate; concrete learns per recipe below) ---- */}
          {store.lens !== "concrete" && <EstimateAccuracyCard store={store} />}

          {/* ---- the concrete lens: the recipe check (what the logged lifts say the card should be) ---- */}
          {store.lens === "concrete" && <RecipeCheckCard store={store} />}
        </>
      )}
    </div>
  );
}

const CAT_COLORS = ["var(--teal)", "var(--cyan)", "var(--violet)", "var(--amber)", "var(--green)", "var(--blue)"];

/* Lead sources & marketing ROI — per-channel leads/won/close/revenue, plus
   optional monthly ad spend the operator types in to get CAC & ROAS. Spend is
   committed on blur (not every keystroke) so it doesn't hammer cloud sync. */
function LeadSourceCard({ report, store }: any) {
  const saved = (store.settings && store.settings.marketingSpend) || {};
  const [draft, setDraft] = React.useState<any>({});
  const val = (k: string) => (draft[k] !== undefined ? draft[k] : (saved[k] != null ? String(saved[k]) : ""));
  const commit = (k: string) => {
    if (draft[k] === undefined) return;
    const n = Math.max(0, Number(draft[k]) || 0);
    store.setSettings({ marketingSpend: { ...saved, [k]: n } });
    setDraft((d: any) => { const c = { ...d }; delete c[k]; return c; });
  };
  const rows = report.sources || [];
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker">Lead Sources &amp; Marketing ROI</div>
        <div className="muted" style={{ fontSize: 12 }}>Where jobs come from · type monthly spend for CAC &amp; ROAS</div>
      </div>
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: "26px 0", margin: 0 }}>No quotes in this window yet.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="rep-src-table">
            <thead><tr><th>Source</th><th className="num">Leads</th><th className="num">Won</th><th className="num">Close</th><th className="num">Revenue</th><th className="num">Spend/mo</th><th className="num">CAC</th><th className="num">ROAS</th></tr></thead>
            <tbody>
              {rows.map((s: any) => (
                <tr key={s.key}>
                  <td>{s.label}</td>
                  <td className="num tnum">{s.leads}</td>
                  <td className="num tnum">{s.won}</td>
                  <td className="num tnum">{s.closeRate}%</td>
                  <td className="num tnum" style={{ color: "var(--teal)", fontWeight: 700 }}>{R_money0(s.revenue)}</td>
                  <td className="num"><span className="rep-spend"><span>$</span><input className="tnum" type="number" min="0" inputMode="decimal" value={val(s.key)} placeholder="0" onChange={e => setDraft((d: any) => ({ ...d, [s.key]: e.target.value }))} onBlur={() => commit(s.key)} onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} /></span></td>
                  <td className="num tnum">{s.cac != null ? R_money0(s.cac) : "—"}</td>
                  <td className="num tnum" style={{ fontWeight: 700, color: s.roas != null ? (s.roas >= 3 ? "var(--green)" : s.roas >= 1 ? "var(--amber)" : "var(--red)") : "var(--fg-3)" }}>{s.roas != null ? s.roas.toFixed(1) + "×" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!report.anyTagged && rows.length > 0
        ? <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Tag a <b style={{ color: "var(--fg-3)" }}>lead source</b> on each estimate (Estimator → Customer &amp; Job Info) to break this down by channel. Public/website estimates are auto-tagged.</p>
        : <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Spend is monthly; CAC &amp; ROAS scale it across the {report.pMonths}-month window. CAC = spend ÷ jobs won · ROAS = revenue ÷ spend.</p>}
    </div>
  );
}

/* Why we lose — the flip side of the close-rate donut. Every lost deal carries
   a reason chip (quote page → "Mark lost"); this rolls them up so two weeks of
   honest tagging answers "are we losing on price or on speed?". */
function WhyWeLoseCard({ report }: any) {
  const rows = report.lostReasons || [];
  const max = rows.reduce((m: number, r: any) => Math.max(m, r.count), 0);
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker"><Icon name="x" size={13} /> Why We Lose</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {report.lostCount > 0
            ? <>{report.lostCount} lost · {R_money0(report.lostTotal)} walked{report.decidedRate != null ? <> · decided close rate <b style={{ color: report.decidedRate >= 50 ? "var(--green)" : "var(--amber)" }}>{report.decidedRate}%</b></> : null}</>
            : "Lost-deal reasons land here"}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: 0, padding: "10px 0" }}>
          No lost deals logged in this window. When a quote dies, open it and hit <b style={{ color: "var(--fg-2)" }}>"Didn't win it? Mark lost"</b> — one reason chip per deal and this panel starts telling you whether it's price, speed, or the competition.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 13 }}>
          {rows.map((r: any, i: number) => {
            const pct = max > 0 ? Math.round((r.count / max) * 100) : 0;
            const share = report.lostCount > 0 ? Math.round((r.count / report.lostCount) * 100) : 0;
            return (
              <div key={r.key}>
                <div className="row between" style={{ marginBottom: 6, fontSize: 13.5 }}>
                  <span className="row gap-8" style={{ fontWeight: 600 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: "var(--red)", opacity: 1 - i * 0.13 }} />{r.label}
                    <span className="muted" style={{ fontWeight: 400 }}>· {r.count} deal{r.count === 1 ? "" : "s"}</span>
                  </span>
                  <span className="tnum" style={{ fontWeight: 700 }}>{R_money0(r.total)} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>({share}%)</span></span>
                </div>
                <div className="rep-hbar"><div className="rep-hbar-fill" style={{ width: Math.max(pct, 2) + "%", background: "var(--red)", opacity: 1 - i * 0.13 }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Accountant export — plain CSV downloads of the invoice book and the payment
   ledger for a date range. For the bookkeeper without QuickBooks (the QBO sync
   covers the rest). Numbers are bare (no $) so spreadsheets ingest them clean. */
function csvEsc(v: any) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
function downloadCsv(name: string, rows: any[][]) {
  const csv = rows.map(r => r.map(csvEsc).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function AccountantExportCard({ store }: any) {
  const ymd = (d: Date) => d.toISOString().slice(0, 10);
  const [from, setFrom] = React.useState<any>(() => ymd(new Date(new Date().getFullYear(), 0, 1)));
  const [to, setTo] = React.useState<any>(() => ymd(new Date()));
  const [done, setDone] = React.useState<any>("");
  const fromMs = () => new Date(from + "T00:00:00").getTime();
  const toMs = () => new Date(to + "T23:59:59").getTime();
  const num2 = (n: any) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);
  const dstr = (t: any) => t ? new Date(t).toLocaleDateString("en-US") : "";
  const estNo = (q: any) => "#" + String(q.id).slice(-6).toUpperCase();

  function exportInvoices() {
    const rows: any[][] = [["Estimate", "Customer", "Email", "Address", "Product", "Linear ft", "Created", "Won", "Status", "Total", "Sales tax (incl.)", "Payments received", "Balance due"]];
    (store.quotes || []).filter((q: any) => { const t = Number(q.createdAt) || 0; return t >= fromMs() && t <= toMs(); }).forEach((q: any) => {
      let tax = "";
      try { const v = computeQuoteView(q, store.settings || {}, store.allProducts); tax = num2(v.quote.tax); } catch (e) {}
      const paid = quotePayments(q).paid;
      rows.push([estNo(q), q.customer || "", q.email || "", q.address || "", q.productName || "", Math.round(q.lf || 0), dstr(q.createdAt), dstr(q.wonAt), q.status || "pending",
        num2(q.total), tax, num2(paid), num2(Math.max(0, (Number(q.total) || 0) - paid))]);
    });
    downloadCsv(`fenceworks-invoices-${from}_${to}.csv`, rows);
    setDone(`Invoices CSV — ${rows.length - 1} row${rows.length === 2 ? "" : "s"}`);
  }
  function exportPayments() {
    const rows: any[][] = [["Date", "Customer", "Estimate", "Method", "Kind", "Reference", "Amount", "Recorded by"]];
    (store.quotes || []).forEach((q: any) => {
      const entries: any[] = [
        ...(Array.isArray(q.stripePayments) && q.stripePayments.length
          ? q.stripePayments.map((p: any) => ({ ...p, method: "stripe" }))
          : (Number(q.depositPaid) > 0 ? [{ amount: Number(q.depositPaid), method: "stripe", kind: "deposit", at: q.wonAt || q.createdAt }] : [])),
        ...(Array.isArray(q.payments) ? q.payments : []),
      ];
      entries.forEach((p: any) => {
        const t = Number(p.at || p.createdAt) || 0;
        if (t && (t < fromMs() || t > toMs())) return;
        rows.push([dstr(t || null), q.customer || "", estNo(q),
          p.method === "stripe" ? "Online (Stripe)" : p.method || "other", p.kind || "",
          p.checkNo ? "Check #" + p.checkNo : (p.ref || p.sessionId || ""), num2(p.amount), p.by || ""]);
      });
    });
    downloadCsv(`fenceworks-payments-${from}_${to}.csv`, rows);
    setDone(`Payments CSV — ${rows.length - 1} row${rows.length === 2 ? "" : "s"}`);
  }
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker"><Icon name="clipboard" size={13} /> Accountant Export</div>
        <div className="muted" style={{ fontSize: 12 }}>Invoice book &amp; payment ledger as CSV — for bookkeeping without QuickBooks</div>
      </div>
      <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
        <div><label className="field-label">From</label><input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 150 }} /></div>
        <div><label className="field-label">To</label><input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: 150 }} /></div>
        <button className="btn btn-outline btn-sm" onClick={exportInvoices}><Icon name="clipboard" size={13} />Invoices CSV</button>
        <button className="btn btn-outline btn-sm" onClick={exportPayments}><Icon name="dollar" size={13} />Payments CSV</button>
        {done && <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={11} />{done}</span>}
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Invoices are filtered by created date; payments by payment date. Amounts are plain numbers so Excel/Sheets ingest them without cleanup. Already on QuickBooks? The live sync in Integrations keeps QBO current — this export is the paper trail.</p>
    </div>
  );
}

/* Pricing coach — rule-based bid guidance from close rate + quoted margins.
   Deliberately opinionated so a contractor gets an action, not just a number. */
function PricingCoachCard({ report }: any) {
  const tips: any[] = [];
  const wr = report.winRate, qc = report.quoteCount;
  if (qc >= 4) {
    if (wr >= 70) tips.push({ tone: "warn", icon: "bolt", text: `Your close rate is ${wr}% — unusually high. You're likely leaving money on the table; test raising prices 5–10% on your next bids and watch the rate.` });
    else if (wr <= 25) tips.push({ tone: "bad", icon: "activity", text: `Close rate is ${wr}%. Either pricing is too high or follow-up is too slow — tighten quote follow-up and revisit your pricing.` });
    else tips.push({ tone: "good", icon: "check", text: `Close rate ${wr}% is in a healthy band. Keep testing small price bumps on your strongest leads.` });
  } else {
    tips.push({ tone: "muted", icon: "send", text: `Only ${qc} quote${qc === 1 ? "" : "s"} in this window — send a few more to unlock reliable pricing guidance.` });
  }
  // lost-reason signal: once a few deals carry reasons, the coach gets specific
  const lost = report.lostReasons || [];
  const lostN = report.lostCount || 0;
  if (lostN >= 3) {
    const share = (key: string) => { const r = lost.find((x: any) => x.key === key); return r ? r.count / lostN : 0; };
    if (share("price") >= 0.4) tips.push({ tone: "warn", icon: "dollar", text: `${Math.round(share("price") * 100)}% of lost deals were price. Before cutting margin, try good/better/best options and financing on the quote — most "too expensive" objections are really payment-size objections.` });
    if (share("no_response") >= 0.4) tips.push({ tone: "bad", icon: "clock", text: `${Math.round(share("no_response") * 100)}% of lost deals just went dark — that's a follow-up-speed problem, not a pricing problem. Tighten the quote follow-up sequence and call within a day of sending.` });
    if (share("competitor") >= 0.4) tips.push({ tone: "warn", icon: "target", text: `${Math.round(share("competitor") * 100)}% of losses went to a competitor. Log who and at what price in the lost note — a pattern there is worth a pricing or pitch adjustment.` });
  }
  if (report.avgMargin > 0) tips.push({ tone: report.avgMargin < 35 ? "warn" : "good", icon: "dollar", text: `Average quoted margin is ${report.avgMargin.toFixed(0)}%${report.avgMargin < 35 ? " — thin. Recheck labor hours & material costs in Settings, or raise your markup." : " — solid."}` });
  const low = (report.catMargins || [])[0];
  if (low && report.catMargins.length > 1) tips.push({ tone: low.margin < 35 ? "bad" : "muted", icon: "ruler", text: `${low.cat} is your lowest-margin work at ${low.margin.toFixed(0)}% across ${low.n} quote${low.n === 1 ? "" : "s"}. Price it higher or verify its labor/material cost.` });
  if (report.avgJob > 0) tips.push({ tone: "muted", icon: "target", text: `Average won job is ${R_money0(report.avgJob)}. Good/better/best options and add-ons are the fastest way to raise this.` });
  const toneCol: any = { good: "var(--green)", warn: "var(--amber)", bad: "var(--red)", muted: "var(--fg-3)" };
  const toneBg: any = { good: "var(--green-tint)", warn: "var(--amber-tint)", bad: "var(--red-tint)", muted: "var(--surface-2)" };
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 14 }}>
        <div className="kicker kicker-teal"><Icon name="bolt" size={13} /> Pricing Coach</div>
        <div className="muted" style={{ fontSize: 12 }}>Data-driven bid guidance</div>
      </div>
      <div style={{ display: "grid", gap: 9 }}>
        {tips.map((t, i) => (
          <div key={i} className="rep-tip">
            <span className="rep-tip-ic" style={{ color: toneCol[t.tone], background: toneBg[t.tone] }}><Icon name={t.icon} size={14} /></span>
            <span style={{ fontSize: 13, lineHeight: 1.45 }}>{t.text}</span>
          </div>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "12px 0 0" }}>Guidance sharpens as you log actual hours &amp; expenses — the job P&amp;L feeds back into these tips.</p>
    </div>
  );
}

/* ---- Estimate accuracy: the learning loop -----------------------------------
   Every job with logged hours compares the engine's estimated labor to what the
   crew actually burned. Grouped by product, it shows WHERE bids drift — and the
   weighted drift becomes a one-click labor-rate adjustment, so the next quote
   already carries the correction. Reads computeQuoteView + wo.timeLogs only. */
/* By job type (roadmap §13 items 12 and 17): every concrete quote won inside
   the period — its priced blocks as frozen at the win (q.wonSnapshot), the
   revenue re-based to the lines the customer signed, the crew's clocked hours
   split by each block's share of the estimate, and the foam the lift logs
   recorded — grouped per job type, then the lifts by drop. Reads the
   snapshots first; an older quote without one is priced from today's cards. */
function ConcreteJobTypeCard({ store, from, period }: any) {
  const settings = store.settings || {};
  const data = React.useMemo(() => {
    const rows: any[] = [];
    const wos = store.workOrders || [];
    let snapshots = 0, repriced = 0;
    (store.quotes || []).filter((q: any) => q && verticalOf(q) === "concrete" && WON_STATUSES.includes(q.status) && inPeriod(q, from)).forEach((q: any) => {
      let blocks: any[] = q.wonSnapshot && Array.isArray(q.wonSnapshot.blocks) ? q.wonSnapshot.blocks : [];
      if (blocks.length) snapshots++;
      else {
        try {
          const v: any = computeQuoteView(q, settings);
          const inputs = blocksOf(q.inputs && q.inputs.concrete);
          blocks = (((v && v.quote && v.quote.concrete) || {}).blocks || []).map((b: any, i: number) => ({ jobType: b.jobType, drop: b.jobType === "lift" ? (inputs[i] && inputs[i].drop) || "unsure" : null, amount: b.amount, cost: b.cost, profit: b.profit, laborHours: b.laborHours }));
          if (blocks.length) repriced++;
        } catch (e) { blocks = []; }
      }
      if (!blocks.length) return;
      const wo = wos.find((w: any) => w && w.quoteId === q.id && w.kind !== "warranty");
      let rs = allocateHours(wo ? actualHours(wo) : 0, rebaseBlocks(blocks.map((b: any) => ({ ...b, quoteId: q.id })), signedRevenueByJobType(q.signedDoc && q.signedDoc.lines)));
      if (wo) {
        const a = liftActuals(wo);
        if (a.foamLb > 0 && a.sqftInch > 0) {
          const lifts = rs.filter((r: any) => r.jobType === "lift");
          const tot = lifts.reduce((x: number, r: any) => x + r.amount, 0);
          rs = rs.map((r: any) => (r.jobType !== "lift" ? r : { ...r, foamLb: a.foamLb * (tot > 0 ? r.amount / tot : 1 / lifts.length), sqftInch: a.sqftInch * (tot > 0 ? r.amount / tot : 1 / lifts.length) }));
        }
      }
      rows.push(...rs);
    });
    const foamComp: any = (((rateCardsOf(settings).lift || {}) as any).components || []).find((c: any) => c && c.kind === "material" && c.per === "sqft-inch");
    const byJob = jobTypeRows(rows, { foamCostPerLb: foamComp ? Number(foamComp.cost) || 0 : 0 });
    const byDrop = groupMargin(rows.filter((r: any) => r.jobType === "lift").map((r: any) => ({ ...r, drop: r.drop || "unsure" })), "drop");
    return { byJob, byDrop, rows: rows.length, snapshots, repriced, cardFoam: foamComp ? Number(foamComp.qty) || 0 : 0 };
  }, [store.quotes, store.workOrders, settings, from]);
  if (!data.rows) return null;
  const dropLabel = (id: string) => (DROPS.find((d: any) => d.id === id) || {}).label || id;
  const money0 = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
  const h = (n: number) => (n ? (Math.round(n * 10) / 10).toLocaleString("en-US") : "—");
  function exportCsv() {
    const cols = [{ key: "jobType", label: "Job type" }, { key: "quotes", label: "Quotes" }, { key: "revenue", label: "Revenue" }, { key: "cost", label: "Cost" }, { key: "profit", label: "Profit" }, { key: "pct", label: "Margin %" }, { key: "estHours", label: "Est. crew hrs" }, { key: "actualHours", label: "Clocked hrs" }, { key: "foamPerSqftInch", label: "Foam lb per sq ft-inch" }, { key: "foamCostPerSqftInch", label: "Foam $ per sq ft-inch" }];
    downloadCsvText(`concrete-by-job-type-${period || "all"}`, toCsv(cols, data.byJob.map((r: any) => ({ ...r, jobType: CX_JOB_SHORT[r.jobType] || r.jobType }))));
  }
  return (
    <div className="card pad" style={{ marginTop: 14 }} data-tour="cx-jobtype">
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker kicker-teal"><Icon name="activity" size={13} /> By job type</div>
        <span className="row gap-8">
          <span className="muted" style={{ fontSize: 12 }}>{data.rows} priced line{data.rows === 1 ? "" : "s"} on won quotes{data.repriced ? ` · ${data.repriced} older quote${data.repriced === 1 ? "" : "s"} priced from today's cards` : ""}</span>
          <button className="btn btn-ghost btn-sm" onClick={exportCsv} title="This table as a CSV"><Icon name="clipboard" size={12} />CSV</button>
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 6px" }}>Revenue as signed (overrides and discounts included), cost and profit as priced at the win, the crew's clocked hours against the estimate, and the foam the lift logs recorded against what the lift card assumes{data.cardFoam ? ` (${data.cardFoam} lb per sq ft-inch before waste)` : ""}.</p>
      <div style={{ overflowX: "auto" }}>
        <table className="quote-table" style={{ marginTop: 6 }}>
          <thead><tr><th></th><th style={{ textAlign: "right" }}>Quotes</th><th style={{ textAlign: "right" }}>Revenue</th><th style={{ textAlign: "right" }}>Cost</th><th style={{ textAlign: "right" }}>Profit</th><th style={{ textAlign: "right" }}>Margin</th><th style={{ textAlign: "right" }} title="Estimated crew hours vs clocked">Hrs est · clocked</th><th style={{ textAlign: "right" }} title="Foam per sq ft-inch as it ran, and what it cost">Foam /sq ft-in</th></tr></thead>
          <tbody>
            {data.byJob.map((r: any) => (
              <tr key={r.jobType}>
                <td><b>{CX_JOB_SHORT[r.jobType] || r.jobType}</b></td>
                <td className="tnum" style={{ textAlign: "right" }}>{r.quotes}</td>
                <td className="tnum" style={{ textAlign: "right" }}>{money0(r.revenue)}</td>
                <td className="tnum muted" style={{ textAlign: "right" }}>{money0(r.cost)}</td>
                <td className="tnum" style={{ textAlign: "right" }}>{money0(r.profit)}</td>
                <td className="tnum" style={{ textAlign: "right", fontWeight: 700, color: r.pct >= 40 ? "var(--green)" : r.pct >= 25 ? "var(--amber)" : "var(--red)" }}>{r.pct}%</td>
                <td className="tnum" style={{ textAlign: "right", color: r.actualHours > r.estHours * 1.15 ? "var(--red)" : undefined }}>{h(r.estHours)} · {h(r.actualHours)}</td>
                <td className="tnum" style={{ textAlign: "right" }}>{r.foamPerSqftInch != null ? `${r.foamPerSqftInch.toFixed(3)} lb${r.foamCostPerSqftInch != null ? ` · $${r.foamCostPerSqftInch.toFixed(2)}` : ""}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.byDrop.length > 0 && (
        <>
          <div className="kicker" style={{ margin: "12px 0 0" }}>Lifts by drop</div>
          <table className="quote-table" style={{ marginTop: 6 }}>
            <thead><tr><th></th><th style={{ textAlign: "right" }}>Lines</th><th style={{ textAlign: "right" }}>Revenue</th><th style={{ textAlign: "right" }}>Profit</th><th style={{ textAlign: "right" }}>Margin</th></tr></thead>
            <tbody>{data.byDrop.map((r: any) => <tr key={r.key}><td>{dropLabel(r.key)}</td><td className="tnum" style={{ textAlign: "right" }}>{r.jobs}</td><td className="tnum" style={{ textAlign: "right" }}>{money0(r.revenue)}</td><td className="tnum" style={{ textAlign: "right" }}>{money0(r.profit)}</td><td className="tnum" style={{ textAlign: "right", fontWeight: 700 }}>{r.pct}%</td></tr>)}</tbody>
          </table>
          <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>The deeper lifts should carry the margin — the foam goes up with the inches.</p>
        </>
      )}
    </div>
  );
}

/* All services (§13 item 17): the account that runs fence and concrete sees
   both in one table — quotes, win rate, revenue, pipeline, backlog, finished
   jobs — whichever lens is on. Over the unscoped arrays. */
function AllServicesCard({ report }: any) {
  const money0 = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
  const label = (v: string) => ((VERTICALS as any)[v] && (VERTICALS as any)[v].label) || v;
  const tot = (k: string) => report.rollup.reduce((s: number, r: any) => s + (Number(r[k]) || 0), 0);
  return (
    <div className="card pad" style={{ marginTop: 14 }} data-tour="all-services">
      <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker">All services</div>
        <div className="muted" style={{ fontSize: 12 }}>Fence beside concrete, this period, whichever lens is on</div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="rep-src-table">
          <thead><tr><th>Service</th><th className="num">Quotes</th><th className="num">Won</th><th className="num">Win rate</th><th className="num">Revenue</th><th className="num">Avg job</th><th className="num">Pipeline</th><th className="num">Backlog</th><th className="num">Completed</th></tr></thead>
          <tbody>
            {report.rollup.map((r: any) => (
              <tr key={r.vertical}>
                <td><b>{label(r.vertical)}</b></td>
                <td className="num tnum">{r.quotes}</td><td className="num tnum">{r.won}</td><td className="num tnum">{r.winRate}%</td>
                <td className="num tnum" style={{ color: "var(--teal)", fontWeight: 700 }}>{money0(r.revenue)}</td>
                <td className="num tnum">{r.avgJob ? money0(r.avgJob) : "—"}</td><td className="num tnum">{money0(r.pipeline)}</td><td className="num tnum">{money0(r.backlog)}</td><td className="num tnum">{r.completed}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700 }}><td>Together</td><td className="num tnum">{tot("quotes")}</td><td className="num tnum">{tot("won")}</td><td className="num tnum">{tot("quotes") ? Math.round((tot("won") / tot("quotes")) * 100) : 0}%</td><td className="num tnum">{money0(tot("revenue"))}</td><td className="num tnum">{tot("won") ? money0(tot("revenue") / tot("won")) : "—"}</td><td className="num tnum">{money0(tot("pipeline"))}</td><td className="num tnum">{money0(tot("backlog"))}</td><td className="num tnum">{tot("completed")}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* The recipe check (roadmap §13 item 12): what the logged lifts say the lift
   card's foam and hours should be, with one tap to make the card say it. */
function RecipeCheckCard({ store }: any) {
  const settings = store.settings || {};
  const [applied, setApplied] = React.useState<any>(false);
  const card = rateCardsOf(settings).lift;
  const out = React.useMemo(() => (card ? suggestRecipe(store.workOrders, card) : null), [store.workOrders, card]);
  if (!card || !out || !out.jobs) return null;
  const canEdit = storeCan(store, "pricebook");
  function apply() {
    const next: any = { ...card, components: out.components, updatedAt: Date.now() };
    ["foamLbPerSqFtInch", "foamCostPerLb", "hoursPerSection", "setupHours", "laborHoursPer100", "materialPerUnit"].forEach((k) => { delete next[k]; });
    store.setSettings({ rateCards: { ...(settings.rateCards || {}), lift: next } });
    store.addActivity && store.addActivity(`Lift recipe updated from ${out.jobs} logged lifts${out.foam && out.foam.suggested != null ? ` — foam ${out.foam.current} → ${out.foam.suggested} lb per sq ft-inch` : ""}`);
    setApplied(true);
  }
  const line = (label: string, r: any, unit: string, digits: number) => r && (
    <div className="row between" style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--line)", gap: 8 }}>
      <span style={{ flex: 1 }}>{label} <span className="faint">· {r.jobs} job{r.jobs === 1 ? "" : "s"}</span></span>
      <span className="tnum">card {r.current.toFixed(digits)} {unit}</span>
      <span className="tnum" style={{ color: r.pct == null ? "var(--fg-3)" : Math.abs(r.pct) <= 10 ? "var(--green)" : "var(--amber)" }}>jobs {r.suggested == null ? "—" : r.suggested.toFixed(digits) + " " + unit}{r.pct != null ? ` (${r.pct > 0 ? "+" : ""}${r.pct}%)` : ""}</span>
    </div>
  );
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker kicker-teal"><Icon name="bolt" size={13} /> Recipe check — the lift card against the lift log</div>
        <span className="muted" style={{ fontSize: 12 }}>{out.jobs} logged lift{out.jobs === 1 ? "" : "s"}</span>
      </div>
      {line("Foam per sq ft-inch", out.foam, "lb", 3)}
      {line("Crew hours per section", out.hours, "hr", 2)}
      <p className="muted" style={{ fontSize: 12.5, margin: "10px 0 0" }}>
        {out.enough
          ? (out.changed ? <>The next estimates would carry what the crews actually use. The foam figure backs the card's waste allowance out; the hours take the per-visit and per-hole labor off the clocked total first.</> : <>The card already matches the jobs within rounding.</>)
          : <>Needs {out.minJobs} finished lifts with a lift log and clocked hours before it suggests a change — the crew logs them in Crew Mode.</>}
      </p>
      {out.enough && out.changed && !applied && canEdit && <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={apply}><Icon name="check" size={12} />Update the lift card</button>}
      {applied && <span className="chip green" style={{ marginTop: 10 }}>Lift card updated</span>}
    </div>
  );
}

function EstimateAccuracyCard({ store }: any) {
  const [applied, setApplied] = React.useState<any>(false);
  const products = store.allProducts || (window.VFP && window.VFP.PRODUCTS) || [];
  const data = React.useMemo(() => {
    const byProd: any = {};
    let estSum = 0, actSum = 0, jobs = 0;
    (store.workOrders || []).forEach((wo: any) => {
      const actual = (Array.isArray(wo.timeLogs) ? wo.timeLogs : []).reduce((a: number, e: any) => a + (Number(e.hours) || 0), 0);
      if (!(actual > 0) || !wo.quoteId) return;
      const q = (store.quotes || []).find((x: any) => x.id === wo.quoteId);
      if (!q) return;
      let est = 0, prodId = q.productId;
      try { const v = computeQuoteView(q, store.settings || {}); est = Number(v && v.quote && v.quote.laborHours) || 0; } catch (e) { return; }
      if (!(est > 0)) return;
      const p = products.find((x: any) => x.id === prodId);
      const key = (p && p.name) || q.productName || "Other";
      const r = byProd[key] || (byProd[key] = { name: key, jobs: 0, est: 0, act: 0 });
      r.jobs++; r.est += est; r.act += actual;
      estSum += est; actSum += actual; jobs++;
    });
    const rows = Object.values(byProd).map((r: any) => ({ ...r, drift: r.est > 0 ? (r.act / r.est - 1) * 100 : 0 }))
      .sort((a: any, b: any) => Math.abs(b.drift) - Math.abs(a.drift));
    return { rows, jobs, drift: estSum > 0 ? (actSum / estSum - 1) * 100 : 0 };
  }, [store.workOrders, store.quotes, store.settings, products]);
  if (data.jobs < 2) return null;   // needs a couple of costed jobs before it says anything
  const rate = Number(store.settings && store.settings.laborRate) || 28;
  const suggested = Math.round(rate * (1 + data.drift / 100));
  const overs = data.drift >= 5 && suggested > rate;
  const unders = data.drift <= -8;
  function applyRate() {
    store.setSettings({ laborRate: suggested });
    store.addActivity(`Labor rate adjusted $${rate} → $${suggested}/hr from estimate-accuracy data (${data.jobs} costed jobs)`);
    setApplied(true);
  }
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker kicker-teal"><Icon name="activity" size={13} /> Estimate Accuracy — quoted vs actual hours</div>
        <span className="muted" style={{ fontSize: 12 }}>{data.jobs} costed job{data.jobs === 1 ? "" : "s"}</span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
        Overall your crews run <b style={{ color: Math.abs(data.drift) < 5 ? "var(--green)" : data.drift > 0 ? "var(--red)" : "var(--amber)" }}>{data.drift >= 0 ? "+" : ""}{data.drift.toFixed(0)}%</b> vs estimated labor hours.
        {overs && !applied && <> Pricing that in: raise the labor rate <b>${rate} → ${suggested}/hr</b> so new quotes carry the real cost.</>}
        {unders && <> You're over-estimating — tightening bids could win more work without losing margin.</>}
      </p>
      {overs && (applied
        ? <div className="pe-callout info" style={{ marginBottom: 12 }}><p style={{ margin: 0, fontSize: 12.5, color: "var(--green)" }}>✓ Labor rate updated to ${suggested}/hr — every new estimate prices with it.</p></div>
        : <button className="btn btn-outline btn-sm" style={{ marginBottom: 12 }} onClick={applyRate}><Icon name="bolt" size={13} />Apply — set labor rate to ${suggested}/hr</button>)}
      <div style={{ overflowX: "auto" }}>
        <table className="rep-src-table">
          <thead><tr><th>Product</th><th className="num">Jobs</th><th className="num">Est hrs</th><th className="num">Actual hrs</th><th className="num">Drift</th></tr></thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.name}>
                <td style={{ fontWeight: 700 }}>{r.name}</td>
                <td className="num tnum">{r.jobs}</td>
                <td className="num tnum">{r.est.toFixed(1)}</td>
                <td className="num tnum">{r.act.toFixed(1)}</td>
                <td className="num"><span className="tnum" style={{ fontWeight: 700, color: Math.abs(r.drift) < 5 ? "var(--green)" : r.drift > 0 ? "var(--red)" : "var(--amber)" }}>{r.drift >= 0 ? "+" : ""}{r.drift.toFixed(0)}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Log crew hours on work orders (Time tracking) and this sharpens every month — your bids literally learn from every job.</p>
    </div>
  );
}

/* Close-rate donut — hand-rolled SVG ring (no chart lib). */
function CloseRate({ winRate, won, quoted }: any) {
  const size = 132, stroke = 13, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, winRate)) / 100) * c;
  return (
    <div style={{ display: "grid", placeItems: "center", padding: "4px 0 8px" }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--surface-3)" strokeWidth={stroke} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--green)" strokeWidth={stroke} fill="none"
            strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset .8s cubic-bezier(.2,.7,.3,1)" }} />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
          <div>
            <div className="display tnum" style={{ fontSize: 32, color: winRate > 0 ? "var(--green)" : "var(--fg-faint)" }}>{winRate}%</div>
            <div className="kicker" style={{ fontSize: 9.5 }}>closed</div>
          </div>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12.5, textAlign: "center", margin: "10px 0 0" }}>
        {won} of {quoted} quote{quoted === 1 ? "" : "s"} won
      </p>
    </div>
  );
}

function PipeRow({ icon, label, sub, value, accent, last }: any) {
  return (
    <div className="row between" style={{ padding: "11px 0", borderBottom: last ? "none" : "1px solid var(--line)" }}>
      <span className="row gap-12" style={{ minWidth: 0 }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line-2)", color: accent }}>
          <Icon name={icon} size={17} />
        </span>
        <span style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</div>
          <div className="muted" style={{ fontSize: 12 }}>{sub}</div>
        </span>
      </span>
      <span className="display tnum" style={{ fontSize: 17, color: accent, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

Object.assign(window, { ReportsPage, CloseRate, PipeRow });

export { ReportsPage, CloseRate, PipeRow };
